// Helpers around the browser Notification API. Centralized so the
// table-order hook (intro on first launch) and the Settings page
// (manual test button) share the same logic.

const INTRO_FIRED_KEY = "xpos-notification-intro-fired";

export function isNotificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export type NotificationPermissionState =
  | "unsupported"
  | "default"
  | "granted"
  | "denied";

export function getNotificationPermission(): NotificationPermissionState {
  if (!isNotificationsSupported()) return "unsupported";
  return Notification.permission;
}

/**
 * Calls Notification.requestPermission(). On Electron this is a
 * no-op that returns "granted" immediately; on browsers it surfaces
 * the OS prompt. Returns the resulting permission state.
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!isNotificationsSupported()) return "unsupported";
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return Notification.permission;
  }
}

/**
 * Fire a notification. Swallows failures (e.g. denied permission)
 * and returns whether the call was attempted; the caller can use
 * this to know whether to show a toast fallback.
 */
export function fireNotification(
  title: string,
  body: string,
  tag?: string,
): boolean {
  if (!isNotificationsSupported()) return false;
  if (Notification.permission !== "granted") return false;
  try {
    new Notification(title, { body, tag, silent: false });
    return true;
  } catch {
    return false;
  }
}

/**
 * Fire the welcome notification exactly once per install. Used to
 * surface the OS-level "Allow notifications?" prompt during the
 * user's first session — Electron's renderer Notification.requestPermission
 * is a no-op, so a real `new Notification()` call is what triggers
 * the macOS / Windows system prompt.
 */
export async function fireIntroNotificationIfNeeded(): Promise<void> {
  if (!isNotificationsSupported()) return;
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(INTRO_FIRED_KEY)) return;

  let perm: NotificationPermissionState = Notification.permission;
  if (perm === "default") {
    perm = await requestNotificationPermission();
  }
  if (perm !== "granted") return;

  const ok = fireNotification(
    "XPOS",
    "Notifications enabled — you'll be alerted when teammates update tables.",
    "xpos-notifications-intro",
  );
  if (ok) localStorage.setItem(INTRO_FIRED_KEY, "1");
}

/**
 * Manual test fire — used by the Settings page's Test button. Does
 * not respect the intro `fired` flag so the user can re-test any
 * time they want.
 */
export function fireTestNotification(): boolean {
  return fireNotification(
    "XPOS",
    "Test notification — if you can read this, notifications are working.",
    "xpos-notifications-test",
  );
}
